import React, { createContext, useContext, useState, useCallback } from 'react';
import { AuthUser, Permission } from '../types/auth';

const TOKEN_KEY = 'pedbot-auth-token';

export const API_BASE =
  process.env.REACT_APP_WORKER_URL ?? 'http://localhost:8787';

const PERMISSION_LEVELS: Record<Permission, number> = {
  viewer: 0,
  editor: 1,
  admin:  2,
};

// ── Types ──────────────────────────────────────────────────

interface AuthContextValue {
  user:          AuthUser | null;
  token:         string | null;
  isLoading:     boolean;
  login:         (email: string, password: string) => Promise<void>;
  logout:        () => void;
  hasPermission: (required: Permission) => boolean;
  sendInvite:    (email: string, role: Permission) => Promise<void>;
}

// ── Token helpers ──────────────────────────────────────────

function decodeToken(token: string): AuthUser | null {
  try {
    const [, payload] = token.split('.');
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );
    if (decoded.exp * 1000 < Date.now()) return null;
    return { email: decoded.email, role: decoded.role };
  } catch {
    return null;
  }
}

function loadFromStorage(): { user: AuthUser | null; token: string | null } {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return { user: null, token: null };
  const user = decodeToken(token);
  if (!user) {
    localStorage.removeItem(TOKEN_KEY);
    return { user: null, token: null };
  }
  return { user, token };
}

// ── Context ────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue>({
  user: null, token: null, isLoading: false,
  login: async () => {},
  logout: () => {},
  hasPermission: () => false,
  sendInvite: async () => {},
});

// ── Provider ───────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const initial = loadFromStorage();
  const [user,      setUser]      = useState<AuthUser | null>(initial.user);
  const [token,     setToken]     = useState<string | null>(initial.token);
  const [isLoading, setIsLoading] = useState(false);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error ?? 'Credenciais inválidas');
      }
      const { token: newToken } = await res.json();
      const newUser = decodeToken(newToken);
      if (!newUser) throw new Error('Token inválido recebido');
      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
      setUser(newUser);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const hasPermission = useCallback(
    (required: Permission): boolean => {
      if (!user) return false;
      return PERMISSION_LEVELS[user.role] >= PERMISSION_LEVELS[required];
    },
    [user]
  );

  const sendInvite = useCallback(
    async (email: string, role: Permission) => {
      if (!token) throw new Error('Não autenticado');
      const res = await fetch(`${API_BASE}/api/auth/invite`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ email, role }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error ?? 'Erro ao enviar convite');
      }
    },
    [token]
  );

  return (
    <AuthContext.Provider
      value={{ user, token, isLoading, login, logout, hasPermission, sendInvite }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
